import {useEffect, useMemo, useState} from 'react';

import {getReviewVideoEmbedType, isUsableVideoPosterUrl} from './review-embed-video-helper';
import {fetchEmbedVideoMetadata} from './review-embed-video-metadata';

type UseReviewEmbedVideoOptionsType = {
    placeholderSrc: string;
    poster?: string | null;
    videoUrl: string;
};

type UseReviewEmbedVideoResultType = {
    isIframePlaceholder: boolean;
    isPreviewLoading: boolean;
    previewSrc: string;
    showDirectVideoMetadataOverlay: boolean;
};

function useEmbedVideoPreviewFetch(
    shouldFetchEmbedPreview: boolean,
    videoUrl: string
): {
    fetchedPreviewSrc: string | null;
    isPreviewLoading: boolean;
} {
    const [fetchedPreviewSrc, setFetchedPreviewSrc] = useState<string | null>(null);
    const [isPreviewLoading, setIsPreviewLoading] = useState(shouldFetchEmbedPreview);

    useEffect(() => {
        let cancelled = false;

        if (!shouldFetchEmbedPreview) {
            setFetchedPreviewSrc(null);
            setIsPreviewLoading(false);
        } else {
            setIsPreviewLoading(true);
            setFetchedPreviewSrc(null);

            fetchEmbedVideoMetadata(videoUrl)
                .then((metadata) => {
                    if (!cancelled) {
                        setFetchedPreviewSrc(metadata?.thumbnailUrl ?? null);
                        setIsPreviewLoading(false);
                    }
                })
                .catch(() => {
                    if (!cancelled) {
                        setFetchedPreviewSrc(null);
                        setIsPreviewLoading(false);
                    }
                });
        }

        return () => {
            cancelled = true;
        };
    }, [shouldFetchEmbedPreview, videoUrl]);

    return {fetchedPreviewSrc, isPreviewLoading};
}

export function useReviewEmbedVideo(options: UseReviewEmbedVideoOptionsType): UseReviewEmbedVideoResultType {
    const {placeholderSrc, poster = null, videoUrl} = options;
    const embedType = getReviewVideoEmbedType(videoUrl);
    const usablePoster = isUsableVideoPosterUrl(poster, videoUrl) ? poster : null;
    const shouldFetchEmbedPreview = embedType === 'iframe' && !usablePoster;

    const {fetchedPreviewSrc, isPreviewLoading} = useEmbedVideoPreviewFetch(shouldFetchEmbedPreview, videoUrl);

    return useMemo(() => {
        const previewSrc = usablePoster || fetchedPreviewSrc || placeholderSrc;
        const hasFetchedThumbnail = Boolean(fetchedPreviewSrc);

        return {
            isIframePlaceholder: embedType === 'iframe' && !usablePoster && !hasFetchedThumbnail && !isPreviewLoading,
            isPreviewLoading: shouldFetchEmbedPreview && isPreviewLoading && !fetchedPreviewSrc,
            previewSrc,
            showDirectVideoMetadataOverlay: !usablePoster && embedType === 'direct',
        };
    }, [embedType, fetchedPreviewSrc, isPreviewLoading, placeholderSrc, shouldFetchEmbedPreview, usablePoster]);
}
